import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';
import { AdminUser, AdminService, AdminPayment } from '../services/adminService';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { useNotification } from '../contexts/NotificationContext';
import { isAdminEmail } from '../config/adminConfig';
import AdminSidebar from './AdminSidebar';
import { sessionService, Session } from '../services/sessionService';
import { activityService, ActivityDocument, userService, userSettingsService, UserProfile, UserSettings } from '../services/firestore';
import { billingService } from '../services/billingService';
import { usePageTitle } from '../hooks/usePageTitle';
import { workspaceService, Workspace } from '../services/workspaceService';
import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

// ── Helper functions defined outside the component to avoid TSX parser issues ──

/**
 * Compute the enforced monthly email limit for a plan.
 * Mirrors functions/subscriptionLimits.js → resolveMaxEmails.
 */
function resolveEnforcedEmailLimit(subscription: any): number {
  if (!subscription) return 12000;
  const plan = String(subscription.plan || '').toLowerCase();
  if (plan === 'business' || plan === 'enterprise') return Number.POSITIVE_INFINITY;
  if (plan === 'starter' || plan === 'free') return 12000;
  const PRO_LIMITS = [
    [1000, 12000], [2500, 25000], [5000, 50000], [8000, 80000],
    [10000, 100000], [15000, 150000], [20000, 200000], [25000, 250000],
    [30000, 300000], [40000, 400000], [50000, 500000], [80000, 800000],
    [100000, 1200000],
  ] as Array<[number, number]>;
  const subs = Number(subscription.subscribers) || 0;
  const match = PRO_LIMITS.find(pair => subs <= pair[0]);
  const computed = match ? match[1] : 1200000;
  const stored = typeof subscription.emails === 'number' ? subscription.emails : 0;
  return Math.max(computed, stored);
}

function EmailQuotaBar({ subscription, used, isDark }: {
  subscription: any; used: number; isDark: boolean;
}) {
  if (!subscription) return null;
  const enforced = resolveEnforcedEmailLimit(subscription);
  const isUnlimited = !isFinite(enforced);
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / enforced) * 100));
  const bar = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-cyan-500';
  const label = isUnlimited ? '\u221e' : enforced.toLocaleString();
  return (
    <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Email quota — current month
        </span>
        <span className={`text-sm font-semibold ${pct >= 90 ? 'text-red-500' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {used.toLocaleString()} / {label}{isUnlimited ? '' : ` (${pct}%)`}
        </span>
      </div>
      {!isUnlimited && (
        <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div className={`h-full rounded-full transition-all ${bar}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

function EmailLimitHint({ subscription, storedEmails, isDark }: {
  subscription: any; storedEmails: number; isDark: boolean;
}) {
  const enforced = resolveEnforcedEmailLimit(subscription);
  const isUnlimited = !isFinite(enforced);
  const label = isUnlimited ? '\u221e' : enforced.toLocaleString();
  const diff = !isUnlimited && enforced !== storedEmails;
  return (
    <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
      Stored cap: <strong>{storedEmails.toLocaleString()}</strong>.{' '}
      Enforced: <strong className={diff ? 'text-amber-500' : ''}>{label}</strong>{isUnlimited ? '' : '/month'}.
      {diff && <span className="ml-1 text-amber-500"> (Pro tier gives {label})</span>}
    </p>
  );
}

function SignupAttributionCard({ user, isDark }: { user: any; isDark: boolean }) {
  if (!user) return null;
  const items = [
    { label: 'Source',   value: user.signupSource },
    { label: 'Label',    value: user.signupSourceLabel },
    { label: 'Badge',    value: user.signupSourceBadge },
    { label: 'Campaign', value: user.signupSourceCampaign },
  ].filter(i => Boolean(i.value));
  if (!items.length) return null;
  return (
    <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="p-6">
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Signup Attribution
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map(item => (
            <div key={item.label} className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</div>
              <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{String(item.value)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface WorkspaceWithStats extends Workspace {
  totalCampaigns?: number;
  totalContacts?: number;
}

type SubscriptionPlan = 'starter' | 'pro' | 'business' | 'enterprise';
type SubscriptionStatus = 'active' | 'inactive' | 'cancelled' | 'past_due' | 'unpaid';

const VALID_SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  'active',
  'inactive',
  'cancelled',
  'past_due',
  'unpaid'
];

const normalizePlan = (plan?: string | null): SubscriptionPlan => {
  const p = (plan || '').toLowerCase();
  if (p === 'pro') return 'pro';
  if (p === 'business') return 'business';
  if (p === 'enterprise') return 'enterprise';
  return 'starter';
};

const labelForPlan = (plan?: string | null): string => {
  const p = normalizePlan(plan);
  if (p === 'pro') return 'Pro';
  if (p === 'business') return 'Business';
  if (p === 'enterprise') return 'Enterprise';
  return 'Starter';
};

const normalizeStatus = (status?: string | null): SubscriptionStatus =>
  VALID_SUBSCRIPTION_STATUSES.includes((status || '').toLowerCase() as SubscriptionStatus)
    ? ((status || '').toLowerCase() as SubscriptionStatus)
    : 'inactive';

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value < 10 && i > 0 ? value.toFixed(2) : Math.round(value)} ${units[i]}`;
};

const parseStorageLabel = (label: string): number => {
  const match = String(label).trim().match(/^([\d.]+)\s*([A-Za-z]+)$/);
  if (!match) return 0;
  const n = parseFloat(match[1]);
  const unit = match[2].toUpperCase();
  const mult: Record<string, number> = {
    B: 1,
    KB: 1024,
    MB: 1024 ** 2,
    GB: 1024 ** 3,
    TB: 1024 ** 4,
  };
  return n * (mult[unit] || 1);
};

const storageUsagePercent = (used: string, limit: string): number | null => {
  if (limit === '—') return null;
  const total = parseStorageLabel(limit);
  if (total <= 0) return null;
  return Math.min(100, Math.round((parseStorageLabel(used) / total) * 100));
};

const UserDetailsPage: React.FC = () => {
  usePageTitle('User Details');
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const { isAdmin, isCheckingAdmin, setImpersonatedUserId } = useAdmin();
  const { isDark, toggleTheme } = useAdminTheme();
  const { showError, showSuccess } = useNotification();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [contacts, setContacts] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactStats, setContactStats] = useState({
    total: 0,
    active: 0,
    unsubscribed: 0,
    bounced: 0,
    imported: 0,
    exported: 0
  });
  const [userMetrics, setUserMetrics] = useState({
    totalCampaigns: 0,
    sentCampaigns: 0,
    draftCampaigns: 0,
    scheduledCampaigns: 0,
    totalEmailsSent: 0,
    lastCampaignDate: null as Date | null,
    subscriptionStatus: 'inactive',
    subscriptionPlan: 'starter',
    monthlyRevenue: 0,
    currentMonthEmailUsage: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [contactsPerPage] = useState(10);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [activitiesPerPage] = useState(10);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsPerPage] = useState(5);
  const [profileSettings, setProfileSettings] = useState({
    emailNotifications: false,
    smsNotifications: false,
    marketingEmails: false,
    timezone: 'UTC',
    language: 'English',
    theme: 'system',
    apiKeys: 0,
    webhooks: 0,
    integrations: [] as string[],
    storageUsed: '0 B',
    storageLimit: '—',
    planType: 'Starter',
    billingCycle: 'N/A',
    paymentMethod: 'No payment method on file',
    nextBillingDate: null as Date | null,
  });
  const [profileSettingsLoaded, setProfileSettingsLoaded] = useState(false);
  const [exportHistory, setExportHistory] = useState<Record<string, unknown>[]>([]);
  const [importHistory, setImportHistory] = useState<Record<string, unknown>[]>([]);
  const [importExportLoading, setImportExportLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [lastOnline, setLastOnline] = useState<Date | null>(null);
  const [subscriptionForm, setSubscriptionForm] = useState<{
    plan: SubscriptionPlan;
    status: SubscriptionStatus;
    subscribers: number;
    emails: number;
  }>({
    plan: 'starter',
    status: 'inactive',
    subscribers: 1000,
    emails: 12000  // STARTER_EMAILS — matches functions/subscriptionLimits.js
  });
  const [subscriptionFormLoading, setSubscriptionFormLoading] = useState(false);
  const [subscriptionFormDirty, setSubscriptionFormDirty] = useState(false);
  const [activities, setActivities] = useState<ActivityDocument[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [payments, setPayments] = useState<AdminPayment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [emails, setEmails] = useState<{
    id: string;
    to: string;
    subject: string;
    from: string;
    type: string;
    status: 'sent' | 'failed';
    messageId?: string;
    timestamp: any;
  }[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [workspaces, setWorkspaces] = useState<WorkspaceWithStats[]>([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);

  // New sections state
  const [automations, setAutomations] = useState<any[]>([]);
  const [automationsLoading, setAutomationsLoading] = useState(false);
  const [senderEmails, setSenderEmails] = useState<any[]>([]);
  const [senderDomains, setSenderDomains] = useState<any[]>([]);
  const [sendersLoading, setSendersLoading] = useState(false);
  const [apiKeysList, setApiKeysList] = useState<any[]>([]);
  const [apiKeysListLoading, setApiKeysListLoading] = useState(false);
  const [abuseFlags, setAbuseFlags] = useState<any[]>([]);
  const [abuseFlagsLoading, setAbuseFlagsLoading] = useState(false);
  const [adminActions, setAdminActions] = useState<any[]>([]);
  const [adminActionsLoading, setAdminActionsLoading] = useState(false);

  console.log('UserDetailsPage rendered with userId:', userId);
  console.log('Auth state - currentUser:', !!currentUser, 'isAdmin:', isAdmin, 'authLoading:', authLoading, 'isCheckingAdmin:', isCheckingAdmin);

  // Define loadUserDetails function before useEffect
  const loadUserDetails = async () => {
    if (!userId) return;

    try {
      console.log('Loading user details for userId:', userId);
      setLoading(true);
      setError(null);

      // Use the correct method to get user by ID
      const foundUser = await AdminService.getUserById(userId);
      console.log('Found user:', foundUser);

      if (foundUser) {
        setUser(foundUser);

        // Check if user is online by checking active sessions
        try {
          const sessions = await sessionService.getUserSessions(userId);
          const activeSessions = sessions.filter(s => s.isActive);

          if (activeSessions.length > 0) {
            // User is considered online if they have an active session with activity in last 5 minutes
            const mostRecentSession = activeSessions[0]; // Already sorted by lastActivity
            const lastActivityTime = mostRecentSession.lastActivity?.toDate();
            const now = new Date();
            const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

            if (lastActivityTime && lastActivityTime > fiveMinutesAgo) {
              setIsOnline(true);
              setLastOnline(lastActivityTime);
            } else {
              setIsOnline(false);
              setLastOnline(lastActivityTime || null);
            }
          } else {
            setIsOnline(false);
            // Use lastLoginAt or lastActive as fallback
            const lastActive = foundUser.lastLoginAt?.toDate() || foundUser.lastActive?.toDate();
            setLastOnline(lastActive || null);
          }
        } catch (sessionError) {
          console.warn('Failed to load session data:', sessionError);
          // Fallback to lastActive/lastLoginAt
          const lastActive = foundUser.lastLoginAt?.toDate() || foundUser.lastActive?.toDate();
          setLastOnline(lastActive || null);
          setIsOnline(false);
        }
      } else {
        setError('User not found');
      }
    } catch (error) {
      console.error('Error loading user details:', error);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  // Load contacts data and user metrics
  const loadContactsData = async () => {
    if (!userId) return;

    try {
      setContactsLoading(true);

      // Fetch real contacts data and user metrics from AdminService
      const [contacts, metrics] = await Promise.all([
        AdminService.getUserContacts(userId),
        AdminService.getUserMetrics(userId)
      ]);

      setContacts(contacts);

      // Calculate stats from real data
      const stats = {
        total: contacts.length,
        active: contacts.filter(c => c.status === 'active').length,
        unsubscribed: contacts.filter(c => c.status === 'unsubscribed').length,
        bounced: contacts.filter(c => c.status === 'bounced').length,
        imported: contacts.filter(c => c.source === 'import').length,
        exported: contacts.filter(c => c.exported).length
      };

      setContactStats(stats);
      setUserMetrics({
        totalCampaigns: metrics.totalCampaigns,
        sentCampaigns: (metrics as any).sentCampaigns ?? 0,
        draftCampaigns: (metrics as any).draftCampaigns ?? 0,
        scheduledCampaigns: (metrics as any).scheduledCampaigns ?? 0,
        totalEmailsSent: metrics.totalEmailsSent,
        lastCampaignDate: metrics.lastCampaignDate,
        subscriptionStatus: metrics.subscriptionStatus,
        subscriptionPlan: metrics.subscriptionPlan,
        monthlyRevenue: metrics.monthlyRevenue,
        currentMonthEmailUsage: (metrics as any).currentMonthEmailUsage ?? 0,
      });
    } catch (error) {
      console.error('Error loading contacts data:', error);
    } finally {
      setContactsLoading(false);
    }
  };

  // Load user activities
  const loadActivities = async () => {
    if (!userId) return;

    try {
      setActivitiesLoading(true);
      const userActivities = await activityService.getUserActivities(userId, 100);
      setActivities(userActivities);
    } catch (error) {
      console.error('Error loading user activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  // Load user payment history
  const loadPaymentHistory = async () => {
    if (!userId) return;

    try {
      setPaymentsLoading(true);
      const userPayments = await AdminService.getUserPaymentHistory(userId);
      setPayments(userPayments);
    } catch (error) {
      console.error('Error loading payment history:', error);
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Load user sessions
  const loadSessions = async () => {
    if (!userId) return;

    try {
      setSessionsLoading(true);
      const userSessions = await sessionService.getUserSessions(userId);
      // Sort sessions: active first, then by lastActivity (most recent first)
      const sortedSessions = [...userSessions].sort((a, b) => {
        if (a.isActive && !b.isActive) return -1;
        if (!a.isActive && b.isActive) return 1;
        const timeA = a.lastActivity?.toDate ? a.lastActivity.toDate().getTime() : 0;
        const timeB = b.lastActivity?.toDate ? b.lastActivity.toDate().getTime() : 0;
        return timeB - timeA;
      });
      setSessions(sortedSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  // Force logout a specific session
  const handleForceLogout = async (sessionId: string) => {
    try {
      await sessionService.deactivateSession(sessionId);
      showSuccess('Session logged out successfully');
      loadSessions(); // Refresh the sessions list
    } catch (error) {
      console.error('Error logging out session:', error);
      showError('Failed to logout session');
    }
  };

  // Force logout all sessions
  const handleForceLogoutAll = async () => {
    if (!userId) return;

    try {
      const activeSessions = sessions.filter(s => s.isActive);
      await Promise.all(
        activeSessions.filter(s => s.id).map(s => sessionService.deactivateSession(s.id!))
      );
      showSuccess(`Logged out ${activeSessions.length} sessions`);
      loadSessions();
      showSuccess(`Logged out ${activeSessions.length} sessions`);
      loadSessions();
    } catch (error) {
      console.error('Error logging out all sessions:', error);
      showError('Failed to logout all sessions');
    }
  };

  // Load user workspaces
  const loadWorkspaces = async () => {
    if (!userId) return;

    try {
      setWorkspacesLoading(true);
      const userWorkspaces = await workspaceService.getUserWorkspaces(userId);

      if (userWorkspaces.length === 0) {
        setWorkspaces([]);
        setWorkspacesLoading(false);
        return;
      }

      // Query per-workspace for accurate counts (not just userId-stamped docs)
      const workspacesWithStats = await Promise.all(userWorkspaces.map(async workspace => {
        const wsId = workspace.id!;
        const [wsContacts, wsCampaigns] = await Promise.all([
          getDocs(query(collection(db, 'contacts'), where('workspaceId', '==', wsId))),
          getDocs(query(collection(db, 'campaigns'), where('workspaceId', '==', wsId))),
        ]);
        return { ...workspace, totalCampaigns: wsCampaigns.size, totalContacts: wsContacts.size };
      }));

      setWorkspaces(workspacesWithStats);
    } catch (error) {
      console.error('Error loading workspaces:', error);
    } finally {
      setWorkspacesLoading(false);
    }
  };

  // Load user email history
  const loadEmailHistory = async () => {
    if (!userId) return;

    try {
      setEmailsLoading(true);
      const userEmails = await AdminService.getUserEmailHistory(userId);
      setEmails(userEmails);
    } catch (error) {
      console.error('Error loading email history:', error);
    } finally {
      setEmailsLoading(false);
    }
  };

  // Handle sending admin email to user
  const handleSendEmail = async () => {
    if (!user?.email || !emailForm.subject.trim() || !emailForm.body.trim()) {
      showError('Please fill in all fields');
      return;
    }

    try {
      setSendingEmail(true);
      const result = await AdminService.sendAdminEmail({
        userId: userId!,
        userEmail: user.email,
        subject: emailForm.subject,
        htmlBody: emailForm.body.replace(/\n/g, '<br>')
      });

      if (result.success) {
        showSuccess('Email sent successfully!');
        setShowEmailModal(false);
        setEmailForm({ subject: '', body: '' });
        loadEmailHistory(); // Refresh email history
      } else {
        showError(result.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      showError('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle force password reset
  const handleForcePasswordReset = async () => {
    if (!user?.email) {
      showError('User email not found');
      return;
    }

    try {
      const result = await AdminService.forcePasswordReset(user.email);
      if (result.success) {
        showSuccess('Password reset email sent successfully!');
        loadEmailHistory(); // Refresh email history
      } else {
        showError(result.error || 'Failed to send password reset email');
      }
    } catch (error) {
      console.error('Error forcing password reset:', error);
      showError('Failed to send password reset email');
    }
  };

  // Handle force verify email
  const handleForceVerifyEmail = async () => {
    if (!userId) return;

    try {
      const result = await AdminService.forceVerifyEmail(userId);
      if (result.success) {
        showSuccess('Email verified successfully!');
        // Refresh user data
        const updatedUser = await AdminService.getUserById(userId);
        if (updatedUser) {
          setUser(updatedUser);
        }
        loadEmailHistory(); // Refresh email history
      } else {
        showError(result.error || 'Failed to verify email');
      }
    } catch (error) {
      console.error('Error verifying email:', error);
      showError('Failed to verify email');
    }
  };

  // Handle exporting all user data
  const handleExportData = async () => {
    if (!userId) return;

    try {
      showSuccess('Preparing data export...');
      const result = await AdminService.exportUserData(userId);

      if (result.success && result.data) {
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `user_${userId}_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showSuccess('Data exported successfully!');
      } else {
        showError(result.error || 'Failed to export data');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      showError('Failed to export data');
    }
  };

  const loadAutomations = async () => {
    if (!userId) return;
    setAutomationsLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'automations'), where('userId', '==', userId),
        orderBy('createdAt', 'desc'), limit(20)
      ));
      setAutomations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch { setAutomations([]); }
    finally { setAutomationsLoading(false); }
  };

  const loadSenders = async () => {
    if (!userId) return;
    setSendersLoading(true);
    try {
      const [emailsSnap, domainsSnap] = await Promise.all([
        getDocs(query(collection(db, 'emailVerifications'), where('userId', '==', userId))).catch(() => null),
        getDocs(query(collection(db, 'domainVerifications'), where('userId', '==', userId))).catch(() => null),
      ]);
      setSenderEmails((emailsSnap?.docs || []).map(d => ({ id: d.id, ...d.data() })));
      setSenderDomains((domainsSnap?.docs || []).map(d => ({ id: d.id, ...d.data() })));
    } catch { setSenderEmails([]); setSenderDomains([]); }
    finally { setSendersLoading(false); }
  };

  const loadApiKeysList = async () => {
    if (!userId) return;
    setApiKeysListLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'oauthTokens'), where('userId', '==', userId), where('type', '==', 'personal')
      ));
      setApiKeysList(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a: any, b: any) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)));
    } catch { setApiKeysList([]); }
    finally { setApiKeysListLoading(false); }
  };

  const loadAbuseFlags = async () => {
    if (!userId) return;
    setAbuseFlagsLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'api_abuse_flags'), where('userId', '==', userId),
        orderBy('flaggedAt', 'desc'), limit(20)
      ));
      setAbuseFlags(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch { setAbuseFlags([]); }
    finally { setAbuseFlagsLoading(false); }
  };

  const loadAdminActions = async () => {
    if (!userId) return;
    setAdminActionsLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'admin_actions'), where('userId', '==', userId),
        orderBy('timestamp', 'desc'), limit(50)
      ));
      setAdminActions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch { setAdminActions([]); }
    finally { setAdminActionsLoading(false); }
  };

  const loadImportExportHistory = async () => {
    if (!userId) return;
    setImportExportLoading(true);
    try {
      const [exports, imports] = await Promise.all([
        AdminService.getUserExportHistory(userId, 10),
        AdminService.getUserImportHistory(userId, 10),
      ]);
      setExportHistory(exports);
      setImportHistory(imports);
    } catch (error) {
      console.error('Error loading import/export history:', error);
      setExportHistory([]);
      setImportHistory([]);
    } finally {
      setImportExportLoading(false);
    }
  };

  // Load user profile settings and related data
  const loadProfileSettings = async () => {
    if (!userId) return;

    try {
      const [profile, settings, billingInfo, supplemental] = await Promise.all([
        userService.getUserProfile(userId),
        userSettingsService.getUserSettings(userId),
        billingService.getBillingInfo(userId).catch(() => null),
        AdminService.getUserSupplementalDetails(userId).catch(() => null),
      ]);

      const subscription = user?.subscription || profile?.subscription || settings?.subscription;
      const planType = labelForPlan(subscription?.plan || 'starter');
      const billingCycle =
        (subscription as { billingPeriod?: string })?.billingPeriod === 'annual'
          ? 'Annual'
          : subscription?.status === 'active'
            ? 'Monthly'
            : 'N/A';

      let nextBillingDate: Date | null = null;
      if (billingInfo?.nextBillingDate) {
        nextBillingDate =
          billingInfo.nextBillingDate instanceof Date
            ? billingInfo.nextBillingDate
            : new Date(billingInfo.nextBillingDate);
      } else if (subscription?.currentPeriodEnd) {
        if (subscription.currentPeriodEnd.toDate) {
          nextBillingDate = subscription.currentPeriodEnd.toDate();
        } else if (subscription.currentPeriodEnd instanceof Date) {
          nextBillingDate = subscription.currentPeriodEnd;
        } else {
          const timestamp = subscription.currentPeriodEnd as { seconds?: number };
          if (timestamp?.seconds) nextBillingDate = new Date(timestamp.seconds * 1000);
        }
      }

      let paymentMethod = 'No payment method on file';
      if (billingInfo?.paymentMethods && billingInfo.paymentMethods.length > 0) {
        const defaultMethod =
          billingInfo.paymentMethods.find((pm) => pm.is_default) || billingInfo.paymentMethods[0];
        if (defaultMethod.type === 'card' && defaultMethod.card) {
          paymentMethod = `${defaultMethod.card.brand || 'Card'} ending in ${defaultMethod.card.last4 || '****'}`;
        } else {
          paymentMethod = defaultMethod.type || 'Payment method';
        }
      }

      const storageUsed = supplemental?.storageUsedBytes ?? 0;
      const storageLimit = supplemental?.storageLimitBytes ?? 0;

      setProfileSettings({
        emailNotifications:
          profile?.emailPreferences?.tips ?? settings?.notifications?.email ?? false,
        smsNotifications: settings?.notifications?.sms ?? false,
        marketingEmails: profile?.emailPreferences?.news ?? false,
        timezone: profile?.timezone || 'UTC',
        language: settings?.language || 'English',
        theme: settings?.theme || 'system',
        apiKeys: supplemental?.apiKeyCount ?? 0,
        webhooks: supplemental?.webhookCount ?? 0,
        integrations: supplemental?.integrationNames?.length
          ? supplemental.integrationNames
          : [],
        storageUsed: formatBytes(storageUsed),
        storageLimit: storageLimit > 0 ? formatBytes(storageLimit) : '—',
        planType,
        billingCycle,
        paymentMethod,
        nextBillingDate,
      });
      setProfileSettingsLoaded(true);
    } catch (error) {
      console.error('Error loading profile settings:', error);
      setProfileSettingsLoaded(true);
    }
  };

  // Load user details effect - must be called before any early returns
  useEffect(() => {
    if (userId) {
      loadUserDetails();
      loadContactsData();
      loadActivities();
      loadPaymentHistory();
      loadSessions();
      loadEmailHistory();
      loadWorkspaces();
      loadImportExportHistory();
      loadAutomations();
      loadSenders();
      loadApiKeysList();
      loadAbuseFlags();
      loadAdminActions();
    } else {
      setError('No user ID provided');
      setLoading(false);
    }
  }, [userId]);

  // Load profile settings after user and contacts are loaded
  useEffect(() => {
    if (user && contacts.length >= 0) {
      loadProfileSettings();
    }
  }, [user, contacts, userMetrics]);

  useEffect(() => {
    if (user?.subscription) {
      const nextPlan = normalizePlan(user.subscription.plan);
      const nextStatus = normalizeStatus(user.subscription.status);
      setSubscriptionForm({
        plan: nextPlan,
        status: nextStatus,
        subscribers: typeof user.subscription.subscribers === 'number' ? user.subscription.subscribers : 0,
        emails: typeof user.subscription.emails === 'number' ? user.subscription.emails : 0,
      });
      setSubscriptionFormDirty(false);
    } else {
      setSubscriptionForm({
        plan: 'starter',
        status: 'inactive',
        subscribers: 0,
        emails: 0,
      });
      setSubscriptionFormDirty(false);
    }
  }, [
    user?.subscription?.plan,
    user?.subscription?.status,
    user?.subscription?.subscribers,
    user?.subscription?.emails
  ]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Pagination logic
  const indexOfLastContact = currentPage * contactsPerPage;
  const indexOfFirstContact = indexOfLastContact - contactsPerPage;
  const currentContacts = contacts.slice(indexOfFirstContact, indexOfLastContact);
  const totalPages = Math.ceil(contacts.length / contactsPerPage);

  // Activities pagination
  const indexOfLastActivity = activitiesPage * activitiesPerPage;
  const indexOfFirstActivity = indexOfLastActivity - activitiesPerPage;
  const currentActivities = activities.slice(indexOfFirstActivity, indexOfLastActivity);

  // Sessions pagination
  const indexOfLastSession = sessionsPage * sessionsPerPage;
  const indexOfFirstSession = indexOfLastSession - sessionsPerPage;
  const currentSessions = sessions.slice(indexOfFirstSession, indexOfLastSession);
  const totalSessionPages = Math.ceil(sessions.length / sessionsPerPage);

  const primaryDevice = useMemo(() => {
    const latest = sessions[0];
    if (latest?.location || latest?.deviceInfo) {
      return {
        ipAddress: latest.location?.ip,
        city: latest.location?.city,
        country: latest.location?.country,
        operatingSystem: latest.deviceInfo?.os,
        browser: latest.deviceInfo?.browser,
        deviceType: latest.deviceInfo?.device,
        userAgent: latest.deviceInfo?.userAgent,
      };
    }
    return user?.deviceInfo;
  }, [sessions, user?.deviceInfo]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleActivitiesPageChange = (pageNumber: number) => {
    setActivitiesPage(pageNumber);
  };

  // Show loading while checking authentication
  if (authLoading || isCheckingAdmin) {
    return (
      <div className={`h-screen ${isDark ? 'bg-black' : 'bg-gray-50'} flex overflow-hidden`}>
        <AdminSidebar
          isDark={isDark}
          activeTab="users"
          onNotificationClick={() => { }}
          alertCount={0}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
            <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Verifying admin access...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if not authenticated
  if (!currentUser || !isAdmin) {
    return (
      <div className={`h-screen ${isDark ? 'bg-black' : 'bg-gray-50'} flex overflow-hidden`}>
        <AdminSidebar
          isDark={isDark}
          activeTab="users"
          onNotificationClick={() => { }}
          alertCount={0}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Access Denied
            </h1>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              You need admin access to view user details.
            </p>
            <button
              onClick={() => navigate('/admin/control-panel-7x9k2m/overview')}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubscriptionInputChange = (
    field: 'plan' | 'status' | 'subscribers' | 'emails',
    value: string
  ) => {
    setSubscriptionForm(prev => {
      let mutated = false;
      let nextState = prev;

      if (field === 'plan') {
        const nextPlan = normalizePlan(value);
        if (nextPlan !== prev.plan) {
          nextState = { ...prev, plan: nextPlan };
          mutated = true;
        }
      } else if (field === 'status') {
        const nextStatus = normalizeStatus(value);
        if (nextStatus !== prev.status) {
          nextState = { ...prev, status: nextStatus };
          mutated = true;
        }
      } else if (field === 'subscribers') {
        const parsedValue = Number(value);
        const nextValue = Number.isFinite(parsedValue) ? Math.max(0, Math.floor(parsedValue)) : 0;
        if (nextValue !== prev.subscribers) {
          nextState = { ...prev, subscribers: nextValue };
          mutated = true;
        }
      } else {
        const parsedValue = Number(value);
        const nextValue = Number.isFinite(parsedValue) ? Math.max(0, Math.floor(parsedValue)) : 0;
        if (nextValue !== prev.emails) {
          nextState = { ...prev, emails: nextValue };
          mutated = true;
        }
      }

      if (mutated) {
        setSubscriptionFormDirty(true);
        return nextState;
      }

      return prev;
    });
  };

  const handleQuickLimitAdjust = (field: 'subscribers' | 'emails', delta: number) => {
    setSubscriptionForm(prev => {
      const nextValue = Math.max(0, Math.floor(prev[field] + delta));
      return { ...prev, [field]: nextValue } as typeof prev;
    });
    setSubscriptionFormDirty(true);
  };

  const resetSubscriptionForm = () => {
    if (user?.subscription) {
      const nextPlan = normalizePlan(user.subscription.plan);
      const nextStatus = normalizeStatus(user.subscription.status);
      const nextSubscribers =
        typeof user.subscription.subscribers === 'number'
          ? user.subscription.subscribers
          : nextPlan === 'starter'
            ? 1000
            : 500;
      const nextEmails =
        typeof user.subscription.emails === 'number'
          ? user.subscription.emails
          : nextPlan === 'starter'
            ? 12000  // STARTER_EMAILS
            : nextPlan === 'business'
              ? 1000000000
              : nextPlan === 'enterprise'
                ? 2000000000
                : 10000;

      setSubscriptionForm({
        plan: nextPlan,
        status: nextStatus,
        subscribers: nextSubscribers,
        emails: nextEmails
      });
    } else {
      setSubscriptionForm({
        plan: 'starter',
        status: 'inactive',
        subscribers: 1000,
        emails: 12000  // STARTER_EMAILS
      });
    }
    setSubscriptionFormDirty(false);
  };

  const handleSubscriptionFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!userId) return;

    setSubscriptionFormLoading(true);
    try {
      await AdminService.updateUserSubscriptionPlan(
        userId,
        subscriptionForm.plan,
        subscriptionForm.status,
        {
          subscribers: subscriptionForm.subscribers,
          emails: subscriptionForm.emails
        }
      );

      showSuccess('Subscription updated successfully');

      const [updatedUser, metrics] = await Promise.all([
        AdminService.getUserById(userId),
        AdminService.getUserMetrics(userId)
      ]);

      if (updatedUser) {
        setUser(updatedUser);
      }

      setUserMetrics({
        totalCampaigns: metrics.totalCampaigns,
        sentCampaigns: (metrics as any).sentCampaigns ?? 0,
        draftCampaigns: (metrics as any).draftCampaigns ?? 0,
        scheduledCampaigns: (metrics as any).scheduledCampaigns ?? 0,
        totalEmailsSent: metrics.totalEmailsSent,
        lastCampaignDate: metrics.lastCampaignDate,
        subscriptionStatus: metrics.subscriptionStatus,
        subscriptionPlan: metrics.subscriptionPlan,
        monthlyRevenue: metrics.monthlyRevenue,
        currentMonthEmailUsage: (metrics as any).currentMonthEmailUsage ?? 0,
      });

      setSubscriptionFormDirty(false);
    } catch (error) {
      console.error('Failed to update subscription details:', error);
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to update subscription details'
      );
    } finally {
      setSubscriptionFormLoading(false);
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Not available';

    let date;
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getDisplayName = (user: AdminUser) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.displayName) {
      return user.displayName;
    }
    return user.email || 'Unknown User';
  };

  const getRoleBadgeColor = (role: string, email: string) => {
    if (isAdminEmail(email)) {
      return isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-100 text-red-800';
    }
    switch (role) {
      case 'admin':
        return isDark ? 'bg-purple-900/20 text-purple-300' : 'bg-purple-100 text-purple-800';
      case 'editor':
        return isDark ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-100 text-blue-800';
      case 'viewer':
        return isDark ? 'bg-gray-900/20 text-gray-300' : 'bg-gray-100 text-gray-800';
      default:
        return isDark ? 'bg-gray-900/20 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return isDark ? 'bg-green-900/20 text-green-300' : 'bg-green-100 text-green-800';
      case 'pending':
        return isDark ? 'bg-yellow-900/20 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'blocked':
        return isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-100 text-red-800';
      default:
        return isDark ? 'bg-gray-900/20 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className={`h-screen ${isDark ? 'bg-black' : 'bg-gray-50'} flex overflow-hidden`}>
        <AdminSidebar
          isDark={isDark}
          activeTab="users"
          onNotificationClick={() => { }}
          alertCount={0}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className={`shadow-sm border-b px-6 py-4 ${isDark
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
            }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h2 className={`text-xl font-semibold capitalize ${isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                  User Details
                </h2>
              </div>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
              <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Loading user details...
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                User ID: {userId}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`h-screen ${isDark ? 'bg-black' : 'bg-gray-50'} flex overflow-hidden`}>
        <AdminSidebar
          isDark={isDark}
          activeTab="users"
          onNotificationClick={() => { }}
          alertCount={0}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className={`shadow-sm border-b px-6 py-4 ${isDark
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
            }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h2 className={`text-xl font-semibold capitalize ${isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                  User Details
                </h2>
              </div>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Error
              </h1>
              <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {error}
              </p>
              <button
                onClick={() => navigate('/admin/control-panel-7x9k2m/overview')}
                className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
              >
                Back to Users
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={`h-screen ${isDark ? 'bg-black' : 'bg-gray-50'} flex overflow-hidden`}>
        <AdminSidebar
          isDark={isDark}
          activeTab="users"
          onNotificationClick={() => { }}
          alertCount={0}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className={`shadow-sm border-b px-6 py-4 ${isDark
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
            }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h2 className={`text-xl font-semibold capitalize ${isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                  User Details
                </h2>
              </div>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                User Not Found
              </h1>
              <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                The requested user could not be found.
              </p>
              <button
                onClick={() => navigate('/admin/control-panel-7x9k2m/overview')}
                className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
              >
                Back to Users
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen ${isDark ? 'bg-black' : 'bg-gray-50'} flex overflow-hidden`}>
      <AdminSidebar
        isDark={isDark}
        activeTab="users"
        onNotificationClick={() => { }}
        alertCount={0}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <div className={`shadow-sm border-b px-6 py-4 ${isDark
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
          }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className={`text-xl font-semibold capitalize ${isDark ? 'text-white' : 'text-gray-900'
                }`}>
                User Details
              </h2>
            </div>
            <div className="flex items-center space-x-4">
              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors duration-200 ${isDark
                  ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Search Bar */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-64 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${isDark
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
                <svg className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Last updated: {currentTime.toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className={`flex-1 overflow-y-auto p-6 ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
          {/* Back Button */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/admin/control-panel-7x9k2m/overview')}
              className={`flex items-center text-sm font-medium ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Users
            </button>
          </div>

          {/* Admin Quick Actions */}
          <div className={`mb-6 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="p-6">
              <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Admin Quick Actions
              </h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    if (setImpersonatedUserId) {
                      setImpersonatedUserId(userId || null);
                    }
                    navigate('/account/profile');
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors inline-flex items-center ${isDark
                    ? 'bg-blue-900/20 border-blue-800 text-blue-300 hover:bg-blue-900/30'
                    : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                    }`}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Enter User Profile
                </button>
                {user.status !== 'blocked' && (
                  <button
                    onClick={async () => {
                      if (window.confirm(`Are you sure you want to block ${user.email}? They will be immediately signed out.`)) {
                        try {
                          await AdminService.blockUser(userId!);
                          showSuccess('User has been blocked successfully');
                          const updatedUser = await AdminService.getUserById(userId!);
                          if (updatedUser) setUser(updatedUser);
                        } catch (error) {
                          showError('Failed to block user');
                        }
                      }
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark
                      ? 'bg-red-900/20 border-red-800 text-red-300 hover:bg-red-900/30'
                      : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                      }`}
                  >
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    Block User
                  </button>
                )}
                {user.status === 'blocked' && (
                  <button
                    onClick={async () => {
                      try {
                        await AdminService.unblockUser(userId!);
                        showSuccess('User has been unblocked successfully');
                        const updatedUser = await AdminService.getUserById(userId!);
                        if (updatedUser) setUser(updatedUser);
                      } catch (error) {
                        showError('Failed to unblock user');
                      }
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark
                      ? 'bg-green-900/20 border-green-800 text-green-300 hover:bg-green-900/30'
                      : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                      }`}
                  >
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Unblock User
                  </button>
                )}
                <button
                  onClick={() => navigate(`/admin/user-contacts/${userId}`)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark
                    ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  View All Contacts
                </button>
                <button
                  onClick={async () => {
                    try {
                      const result = await AdminService.toggleUserPlan(userId!);
                      showSuccess(`Successfully toggled plan to ${result.newPlan.toUpperCase()}`);
                      const updatedUser = await AdminService.getUserById(userId!);
                      if (updatedUser) setUser(updatedUser);
                    } catch (error) {
                      showError('Failed to toggle plan');
                    }
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark
                    ? 'bg-purple-900/20 border-purple-800 text-purple-300 hover:bg-purple-900/30'
                    : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                    }`}
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  Toggle Plan
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(user.id);
                    showSuccess('User ID copied to clipboard');
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark
                    ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copy User ID
                </button>
                <button
                  disabled={deletingUser || (currentUser?.uid === userId)}
                  onClick={async () => {
                    if (currentUser?.uid === userId) {
                      showError('You cannot delete your own admin account while signed in.');
                      return;
                    }
                    const confirmMessage = `PERMANENTLY DELETE ${user.email}?\n\nThis will delete:\n• Firebase Auth account\n• User profile & settings\n• All workspaces they own (contacts, campaigns, automations)\n• Domains, sender emails, sessions, billing records\n• Uploaded files in Storage\n\nThis CANNOT be undone.`;
                    if (window.confirm(confirmMessage)) {
                      const secondConfirm = window.confirm(
                        `Final confirmation: permanently delete "${user.email}" and ALL associated data?`
                      );
                      if (secondConfirm) {
                        setDeletingUser(true);
                        try {
                          const result = await AdminService.deleteUser(userId!);
                          const authNote = result.authDeleted
                            ? 'Auth account removed.'
                            : 'No Firebase Auth account found (profile data was removed).';
                          showSuccess(
                            `User permanently deleted. ${authNote} (${result.totalDocumentOperations} database operations.)`
                          );
                          navigate('/admin/control-panel-7x9k2m/users');
                        } catch (error) {
                          console.error('Failed to delete user:', error);
                          showError(error instanceof Error ? error.message : 'Failed to delete user');
                        } finally {
                          setDeletingUser(false);
                        }
                      }
                    }
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDark
                    ? 'bg-red-900/30 border-red-700 text-red-300 hover:bg-red-900/50'
                    : 'bg-red-100 border-red-300 text-red-700 hover:bg-red-200'
                    }`}
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  {deletingUser ? 'Deleting user…' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>

          {/* User Information */}
          <div className={`rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Basic Information */}
                <div className="space-y-6">
                  <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Basic Information
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Full Name
                      </label>
                      <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {getDisplayName(user)}
                      </p>
                    </div>

                    <div>
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Email Address
                      </label>
                      <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.email}
                      </p>
                    </div>

                    <div>
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Role
                      </label>
                      <div className="mt-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role || 'user', user.email || '')}`}>
                          {user.email && isAdminEmail(user.email) ? 'Administrator' : (user.role || 'User').charAt(0).toUpperCase() + (user.role || 'user').slice(1)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Status
                      </label>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${user.status === 'blocked'
                          ? (isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-800')
                          : isOnline
                            ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800')
                            : (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')
                          }`}>
                          {user.status === 'blocked' ? (
                            <>
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" /></svg>
                              Blocked
                            </>
                          ) : isOnline ? (
                            <>
                              <span className="w-2 h-2 rounded-full bg-current mr-1 animate-pulse"></span>
                              Online
                            </>
                          ) : (
                            <>
                              <span className="w-2 h-2 rounded-full bg-current mr-1"></span>
                              Offline
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-6">
                  <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Additional Information
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        User ID
                      </label>
                      <p className={`text-sm mt-1 font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.id}
                      </p>
                    </div>

                    <div>
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Email Verified
                      </label>
                      <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.emailVerified === true ? 'Yes' : user.emailVerified === false ? 'No' : 'Unknown'}
                      </p>
                    </div>

                    <div>
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Account Type
                      </label>
                      <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.email && isAdminEmail(user.email) ? 'Administrator' : 'Regular User'}
                      </p>
                    </div>

                    <div>
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Created At
                      </label>
                      <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatTimestamp(user.createdAt)}
                      </p>
                    </div>

                    <div>
                      <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        Last Login
                      </label>
                      <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.lastLoginAt ? formatTimestamp(user.lastLoginAt) : 'Never'}
                      </p>
                    </div>


                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Workspaces Table Section */}
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Associated Workspaces
                </h3>
                <span className={`text-sm font-medium px-2.5 py-1 rounded-full ${isDark ? 'bg-cyan-900/30 text-cyan-400' : 'bg-cyan-100 text-cyan-800'}`}>
                  {workspacesLoading ? 'Loading...' : `${workspaces.length} Total`}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className={`text-xs uppercase font-semibold ${isDark ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                    <tr>
                      <th className="px-6 py-4">Workspace Name</th>
                      <th className="px-6 py-4">Created Date</th>
                      <th className="px-6 py-4 text-center">Total Campaigns</th>
                      <th className="px-6 py-4 text-center">Total Contacts</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {workspacesLoading ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                          <div className="flex flex-col items-center justify-center space-y-3">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                            <p>Loading workspaces...</p>
                          </div>
                        </td>
                      </tr>
                    ) : workspaces.length > 0 ? (
                      workspaces.map((ws) => (
                        <tr key={ws.id} className={`transition-colors hover:${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`p-2 rounded-lg mr-3 ${isDark ? 'bg-gray-800 text-cyan-400' : 'bg-cyan-50 text-cyan-600'}`}>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                              </div>
                              <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                {ws.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                              {formatTimestamp(ws.createdAt)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center justify-center min-w-[3rem] px-2.5 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-800'}`}>
                              {ws.totalCampaigns ?? 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center justify-center min-w-[3rem] px-2.5 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-800'}`}>
                              {ws.totalContacts ?? 0}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                          <div className="flex flex-col items-center justify-center text-gray-400">
                            <svg className="w-12 h-12 mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            <p>No associated workspaces found for this user.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Device & Session Information
                </h3>
                <div className="flex items-center space-x-3">
                  {/* Login Status Indicator */}
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                      {isOnline ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Device Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {/* IP Address */}
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    IP Address
                  </div>
                  <div className={`text-base font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {primaryDevice?.ipAddress || 'Not available'}
                  </div>
                </div>

                {/* Location */}
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Location
                  </div>
                  <div className={`text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {primaryDevice?.city && primaryDevice?.country
                      ? `${primaryDevice.city}, ${primaryDevice.country}`
                      : primaryDevice?.country || primaryDevice?.city || 'Not available'}
                  </div>
                </div>

                {/* Operating System */}
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Operating System
                  </div>
                  <div className={`text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {primaryDevice?.operatingSystem || 'Not available'}
                  </div>
                </div>

                {/* Browser */}
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9c0 5-4 9-9 9m9-9H3" />
                    </svg>
                    Browser
                  </div>
                  <div className={`text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {primaryDevice?.browser || 'Not available'}
                  </div>
                </div>

                {/* Device Type */}
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Device Type
                  </div>
                  <div className={`text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {primaryDevice?.deviceType
                      ? primaryDevice.deviceType.charAt(0).toUpperCase() + primaryDevice.deviceType.slice(1)
                      : 'Not available'}
                  </div>
                </div>

                {/* Last Online */}
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Last Online
                  </div>
                  <div className={`text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {lastOnline
                      ? lastOnline.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                      : 'Never'}
                  </div>
                  {lastOnline && !isOnline && (
                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {(() => {
                        const now = new Date();
                        const diffMs = now.getTime() - lastOnline.getTime();
                        const diffMins = Math.floor(diffMs / 60000);
                        const diffHours = Math.floor(diffMs / 3600000);
                        const diffDays = Math.floor(diffMs / 86400000);

                        if (diffMins < 1) return 'Just now';
                        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
                        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
                        if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
                        return 'More than a month ago';
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Device Info */}
              {primaryDevice?.userAgent && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    User Agent {sessions.length > 0 ? '(latest session)' : ''}
                  </div>
                  <div className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-600'} break-all`}>
                    {primaryDevice.userAgent}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Active Sessions Section */}
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  🖥️ Active Sessions
                </h3>
                <div className="flex items-center space-x-3">
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {sessions.filter(s => s.isActive).length} active / {sessions.length} total
                  </span>
                  {sessions.filter(s => s.isActive).length > 0 && (
                    <button
                      onClick={handleForceLogoutAll}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${isDark
                        ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                    >
                      Logout All Sessions
                    </button>
                  )}
                </div>
              </div>

              {sessionsLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto mb-2"></div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading sessions...</p>
                </div>
              ) : sessions.length === 0 ? (
                <div className="p-8 text-center">
                  <svg className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No sessions found</p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>This user has no recorded sessions</p>
                </div>
              ) : (
                <div className="flex flex-col w-full">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className={`text-xs uppercase font-semibold ${isDark ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                        <tr>
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4">Device / Browser</th>
                          <th className="px-6 py-4">IP Address</th>
                          <th className="px-6 py-4">Location</th>
                          <th className="px-6 py-4">Last Activity</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                        {currentSessions.map((session) => (
                          <tr key={session.id} className={session.isActive ? (isDark ? 'bg-green-900/5' : 'bg-green-50/50') : ''}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {session.isActive ? (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                  <span className="w-1.5 h-1.5 rounded-full bg-current mr-1 animate-pulse"></span>
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                  Inactive
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                <div className={`p-1.5 rounded-lg ${session.isActive
                                  ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-600')
                                  : (isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500')
                                  }`}>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {session.deviceInfo?.device?.toLowerCase().includes('mobile') ? (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    ) : (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    )}
                                  </svg>
                                </div>
                                <span className={isDark ? 'text-gray-200' : 'text-gray-700'}>
                                  {session.deviceInfo?.browser || 'Unknown'} on {session.deviceInfo?.os || 'Unknown'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap font-mono text-xs">
                              {session.location?.ip || 'Unknown'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {session.location?.city || 'Unknown'}
                              {session.location?.country && `, ${session.location.country}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-xs">
                              {session.lastActivity?.toDate ? session.lastActivity.toDate().toLocaleString() : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              {session.isActive && session.id && (
                                <button
                                  onClick={() => handleForceLogout(session.id!)}
                                  className={`text-xs font-medium transition-colors ${isDark
                                    ? 'text-red-400 hover:text-red-300'
                                    : 'text-red-600 hover:text-red-800'
                                    }`}
                                >
                                  Force Logout
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Session Pagination Controls */}
                  {totalSessionPages > 1 && (
                    <div className="flex items-center justify-between mt-6 px-2">
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Showing {indexOfFirstSession + 1} to {Math.min(indexOfLastSession, sessions.length)} of {sessions.length} sessions
                      </p>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => setSessionsPage(prev => Math.max(1, prev - 1))}
                          disabled={sessionsPage === 1}
                          className={`p-1 rounded transition-colors ${isDark
                            ? 'hover:bg-gray-800 disabled:opacity-30'
                            : 'hover:bg-gray-100 disabled:opacity-30'
                            }`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setSessionsPage(prev => Math.min(totalSessionPages, prev + 1))}
                          disabled={sessionsPage === totalSessionPages}
                          className={`p-1 rounded transition-colors ${isDark
                            ? 'hover:bg-gray-800 disabled:opacity-30'
                            : 'hover:bg-gray-100 disabled:opacity-30'
                            }`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Subscription Management Section */}
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Subscription Management
                </h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={async () => {
                      try {
                        const result = await AdminService.toggleUserPlan(userId!);
                        showSuccess(`Successfully changed plan to ${result.newPlan.toUpperCase()}`);
                        // Reload user data
                        const updatedUser = await AdminService.getUserById(userId!);
                        if (updatedUser) {
                          setUser(updatedUser);
                        }
                        // Reload metrics
                        const metrics = await AdminService.getUserMetrics(userId!);
                        setUserMetrics({
                          totalCampaigns: metrics.totalCampaigns,
                          sentCampaigns: (metrics as any).sentCampaigns ?? 0,
                          draftCampaigns: (metrics as any).draftCampaigns ?? 0,
                          scheduledCampaigns: (metrics as any).scheduledCampaigns ?? 0,
                          totalEmailsSent: metrics.totalEmailsSent,
                          lastCampaignDate: metrics.lastCampaignDate,
                          subscriptionStatus: metrics.subscriptionStatus,
                          subscriptionPlan: metrics.subscriptionPlan,
                          monthlyRevenue: metrics.monthlyRevenue,
                          currentMonthEmailUsage: (metrics as any).currentMonthEmailUsage ?? 0,
                        });
                      } catch (error) {
                        showError(`Failed to toggle plan: ${error instanceof Error ? error.message : 'Unknown error'}`);
                      }
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isDark
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}
                  >
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Toggle Plan (Starter ⇄ Pro)
                  </button>
                </div>
              </div>

              {/* Subscription Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Current Plan
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${isDark
                      ? normalizePlan(user.subscription?.plan) === 'enterprise'
                        ? 'bg-amber-900/30 text-amber-200'
                        : normalizePlan(user.subscription?.plan) === 'business'
                          ? 'bg-violet-900/30 text-violet-200'
                          : normalizePlan(user.subscription?.plan) === 'pro'
                            ? 'bg-purple-900/20 text-purple-300'
                            : 'bg-gray-700 text-gray-300'
                      : normalizePlan(user.subscription?.plan) === 'enterprise'
                        ? 'bg-amber-100 text-amber-800'
                        : normalizePlan(user.subscription?.plan) === 'business'
                          ? 'bg-violet-100 text-violet-800'
                          : normalizePlan(user.subscription?.plan) === 'pro'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                      {labelForPlan(user.subscription?.plan).toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Subscription Status
                  </div>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${user.subscription?.status === 'active'
                    ? isDark ? 'bg-green-900/20 text-green-300' : 'bg-green-100 text-green-800'
                    : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                    }`}>
                    {user.subscription?.status?.toUpperCase() || 'INACTIVE'}
                  </span>
                </div>

                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Subscribers Limit
                  </div>
                  <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {user.subscription?.subscribers?.toLocaleString() || 0}
                  </div>
                </div>

                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Emails Limit
                  </div>
                  <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {user.subscription?.emails?.toLocaleString() || 0}
                  </div>
                </div>
              </div>

              {/* Subscription Controls */}
              <form onSubmit={handleSubscriptionFormSubmit} className="mt-6">
                <div className={`rounded-lg p-4 space-y-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Plan
                      </label>
                      <select
                        value={subscriptionForm.plan}
                        onChange={(event) => handleSubscriptionInputChange('plan', event.target.value)}
                        disabled={subscriptionFormLoading}
                        className={`mt-2 w-full px-3 py-2 text-sm rounded-lg border focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${isDark
                          ? 'bg-gray-900 border-gray-700 text-white'
                          : 'bg-white border-gray-200 text-gray-900'
                          }`}
                      >
                        <option value="starter">Starter (Free)</option>
                        <option value="pro">Pro</option>
                        <option value="business">Business</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>

                    <div>
                      <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Status
                      </label>
                      <select
                        value={subscriptionForm.status}
                        onChange={(event) => handleSubscriptionInputChange('status', event.target.value)}
                        disabled={subscriptionFormLoading}
                        className={`mt-2 w-full px-3 py-2 text-sm rounded-lg border focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${isDark
                          ? 'bg-gray-900 border-gray-700 text-white'
                          : 'bg-white border-gray-200 text-gray-900'
                          }`}
                      >
                        {VALID_SUBSCRIPTION_STATUSES.map(statusOption => (
                          <option key={statusOption} value={statusOption}>
                            {statusOption.replace('_', ' ').toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Contact Limit
                      </label>
                      <input
                        type="number"
                        min={0}
                        step={100}
                        value={subscriptionForm.subscribers}
                        onChange={(event) => handleSubscriptionInputChange('subscribers', event.target.value)}
                        disabled={subscriptionFormLoading}
                        className={`mt-2 w-full px-3 py-2 text-sm rounded-lg border focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${isDark
                          ? 'bg-gray-900 border-gray-700 text-white'
                          : 'bg-white border-gray-200 text-gray-900'
                          }`}
                      />
                      <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        Maximum contacts/subscribers the user can store.
                      </p>
                    </div>

                    <div>
                      <label className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Email Allowance
                      </label>
                      <input
                        type="number"
                        min={0}
                        step={500}
                        value={subscriptionForm.emails}
                        onChange={(event) => handleSubscriptionInputChange('emails', event.target.value)}
                        disabled={subscriptionFormLoading}
                        className={`mt-2 w-full px-3 py-2 text-sm rounded-lg border focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${isDark
                          ? 'bg-gray-900 border-gray-700 text-white'
                          : 'bg-white border-gray-200 text-gray-900'
                          }`}
                      />
                      <EmailLimitHint
                        subscription={user?.subscription}
                        storedEmails={subscriptionForm.emails}
                        isDark={isDark}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex flex-wrap gap-2">
                      <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        Quick Increase
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={subscriptionFormLoading}
                          onClick={() => handleQuickLimitAdjust('subscribers', 500)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${isDark
                            ? 'bg-gray-900 border-gray-700 text-gray-200 hover:bg-gray-800'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          +500 Contacts
                        </button>
                        <button
                          type="button"
                          disabled={subscriptionFormLoading}
                          onClick={() => handleQuickLimitAdjust('subscribers', 1000)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${isDark
                            ? 'bg-gray-900 border-gray-700 text-gray-200 hover:bg-gray-800'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          +1K Contacts
                        </button>
                        <button
                          type="button"
                          disabled={subscriptionFormLoading}
                          onClick={() => handleQuickLimitAdjust('emails', 1000)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${isDark
                            ? 'bg-gray-900 border-gray-700 text-gray-200 hover:bg-gray-800'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          +1K Emails
                        </button>
                        <button
                          type="button"
                          disabled={subscriptionFormLoading}
                          onClick={() => handleQuickLimitAdjust('emails', 5000)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${isDark
                            ? 'bg-gray-900 border-gray-700 text-gray-200 hover:bg-gray-800'
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          +5K Emails
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={resetSubscriptionForm}
                        disabled={subscriptionFormLoading || !subscriptionFormDirty}
                        className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark
                          ? 'bg-gray-900 border-gray-700 text-gray-200 hover:bg-gray-800 disabled:hover:bg-gray-900 disabled:text-gray-600 disabled:border-gray-800 disabled:cursor-not-allowed'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100 disabled:hover:bg-white disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed'
                          }`}
                      >
                        Reset
                      </button>
                      <button
                        type="submit"
                        disabled={!subscriptionFormDirty || subscriptionFormLoading}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${subscriptionFormDirty && !subscriptionFormLoading
                          ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                          : 'bg-cyan-600/50 text-white/70 cursor-not-allowed disabled:cursor-not-allowed'
                          }`}
                      >
                        {subscriptionFormLoading ? 'Saving...' : 'Update Subscription'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {/* Subscription Timeline */}
              {user.subscription && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Subscription Timeline
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {user.subscription.startDate && (
                      <div>
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Started
                        </div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatTimestamp(user.subscription.startDate)}
                        </div>
                      </div>
                    )}
                    {user.subscription.currentPeriodStart && (
                      <div>
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Current Period Start
                        </div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatTimestamp(user.subscription.currentPeriodStart)}
                        </div>
                      </div>
                    )}
                    {user.subscription.currentPeriodEnd && (
                      <div>
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Current Period End
                        </div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatTimestamp(user.subscription.currentPeriodEnd)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={async () => {
                    try {
                      await AdminService.updateUserSubscriptionPlan(userId!, 'starter', 'active');
                      showSuccess('User downgraded to Free plan');
                      const updatedUser = await AdminService.getUserById(userId!);
                      if (updatedUser) setUser(updatedUser);
                    } catch (error) {
                      showError('Failed to downgrade user');
                    }
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${isDark
                    ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Set to Free
                </button>
                <button
                  onClick={async () => {
                    try {
                      await AdminService.updateUserSubscriptionPlan(userId!, 'pro', 'active');
                      showSuccess('User upgraded to Pro plan');
                      const updatedUser = await AdminService.getUserById(userId!);
                      if (updatedUser) setUser(updatedUser);
                    } catch (error) {
                      showError('Failed to upgrade user');
                    }
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${isDark
                    ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Set to Pro
                </button>
                <button
                  onClick={async () => {
                    try {
                      await AdminService.updateUserSubscriptionPlan(userId!, 'business', 'active');
                      showSuccess('User moved to Business plan');
                      const updatedUser = await AdminService.getUserById(userId!);
                      if (updatedUser) setUser(updatedUser);
                    } catch (error) {
                      showError('Failed to set Business plan');
                    }
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${isDark
                    ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Set to Business
                </button>
                <button
                  onClick={async () => {
                    try {
                      await AdminService.updateUserSubscriptionPlan(userId!, 'enterprise', 'active');
                      showSuccess('User moved to Enterprise plan');
                      const updatedUser = await AdminService.getUserById(userId!);
                      if (updatedUser) setUser(updatedUser);
                    } catch (error) {
                      showError('Failed to set Enterprise plan');
                    }
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${isDark
                    ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Set to Enterprise
                </button>
                <button
                  onClick={async () => {
                    try {
                      if (user.subscription?.plan) {
                        const cancellablePlan = normalizePlan(user.subscription.plan);
                        await AdminService.updateUserSubscriptionPlan(
                          userId!,
                          cancellablePlan,
                          'cancelled'
                        );
                        showSuccess('Subscription cancelled');
                        const updatedUser = await AdminService.getUserById(userId!);
                        if (updatedUser) setUser(updatedUser);
                      }
                    } catch (error) {
                      showError('Failed to cancel subscription');
                    }
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${isDark
                    ? 'bg-red-900/20 border-red-800 text-red-300 hover:bg-red-900/30'
                    : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                    }`}
                >
                  Cancel Subscription
                </button>
              </div>
            </div>
          </div>

          {/* Contacts Management Section */}
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Contacts Management
                </h3>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      if (contacts.length === 0) {
                        showError('No contacts to export');
                        return;
                      }
                      const headers = ['Email', 'First Name', 'Last Name', 'Status'];
                      const rows = contacts.map((c) =>
                        [c.email, c.firstName || '', c.lastName || '', c.status || ''].map((f) =>
                          `"${String(f).replace(/"/g, '""')}"`
                        ).join(',')
                      );
                      const csv = [headers.join(','), ...rows].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `contacts-${userId}-${new Date().toISOString().split('T')[0]}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                      showSuccess(`Exported ${contacts.length} contacts`);
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark
                      ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Contacts (CSV)
                  </button>
                  <button
                    onClick={() => {
                      // In admin context, don't navigate to main app
                      // This feature is disabled in admin panel
                      alert('Contact creation is managed through the admin user management interface.');
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors opacity-50 cursor-not-allowed ${isDark
                      ? 'bg-gray-800 border-gray-600 text-gray-500'
                      : 'bg-white border-gray-300 text-gray-400'
                      }`}
                    disabled
                    title="Contact creation is managed through the admin user management interface"
                  >
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Import Contacts
                  </button>
                  <button
                    onClick={() => navigate(`/admin/user-contacts/${userId}`)}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View All Contacts
                  </button>
                  <button
                    onClick={() => navigate(`/admin/user-contacts/${userId}`)}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    See Added Contacts
                  </button>
                </div>
              </div>

              {/* User Activity Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="text-2xl font-bold text-blue-500">{userMetrics.totalCampaigns}</div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Campaigns</div>
                  {userMetrics.totalCampaigns > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {userMetrics.sentCampaigns > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                          {userMetrics.sentCampaigns} sent
                        </span>
                      )}
                      {userMetrics.draftCampaigns > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {userMetrics.draftCampaigns} draft
                        </span>
                      )}
                      {userMetrics.scheduledCampaigns > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          {userMetrics.scheduledCampaigns} scheduled
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="text-2xl font-bold text-green-500">{userMetrics.totalEmailsSent.toLocaleString()}</div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Emails Sent (all-time)</div>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="text-2xl font-bold text-purple-500">
                    {userMetrics.subscriptionPlan ? userMetrics.subscriptionPlan.charAt(0).toUpperCase() + userMetrics.subscriptionPlan.slice(1) : 'Starter'}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Plan</div>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="text-2xl font-bold text-yellow-500">
                    {userMetrics.monthlyRevenue > 0 ? `$${userMetrics.monthlyRevenue.toFixed(2)}` : '\u2014'}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Revenue (last 30 days)</div>
                </div>
              </div>

              <EmailQuotaBar
                subscription={user?.subscription}
                used={userMetrics.currentMonthEmailUsage}
                isDark={isDark}
              />

              {/* Contact Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {contactStats.total}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Total Contacts
                  </div>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-2xl font-bold text-green-500`}>
                    {contactStats.active}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Active
                  </div>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-2xl font-bold text-yellow-500`}>
                    {contactStats.unsubscribed}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Unsubscribed
                  </div>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-2xl font-bold text-red-500`}>
                    {contactStats.bounced}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Bounced
                  </div>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-2xl font-bold text-blue-500`}>
                    {contactStats.imported}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Imported
                  </div>
                </div>
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className={`text-2xl font-bold text-purple-500`}>
                    {contactStats.exported}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Exported
                  </div>
                </div>
              </div>

              {/* Recent Contacts Table */}
              <div className="mb-6">
                <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Recent Contacts
                </h4>
                {contactsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                  </div>
                ) : (
                  <div className={`overflow-hidden rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                        <tr>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                            Name
                          </th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                            Email
                          </th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                            Status
                          </th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                            Added
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`${isDark ? 'bg-gray-900 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                        {currentContacts.map((contact) => (
                          <tr key={contact.id}>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'
                              }`}>
                              {contact.firstName} {contact.lastName}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'
                              }`}>
                              {contact.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${contact.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : contact.status === 'unsubscribed'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                                }`}>
                                {contact.status ? contact.status.charAt(0).toUpperCase() + contact.status.slice(1) : 'Unknown'}
                              </span>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'
                              }`}>
                              {formatTimestamp(contact.createdAt || (contact as any).addedAt || (contact as any).created || (contact as any).dateAdded) || 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination Controls */}
                {contacts.length > contactsPerPage && (
                  <div className={`px-6 py-4 border-t ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                    }`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className={`text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Showing {indexOfFirstContact + 1} to {Math.min(indexOfLastContact, contacts.length)} of {contacts.length} contacts
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${currentPage === 1
                            ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                            : isDark
                              ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                          Previous
                        </button>

                        <div className="flex items-center space-x-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                              // Show first page, last page, current page, and pages around current
                              return page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 && page <= currentPage + 1);
                            })
                            .map((page, index, array) => {
                              // Add ellipsis if there's a gap
                              const prevPage = array[index - 1];
                              const showEllipsis = prevPage && page - prevPage > 1;
                              return (
                                <React.Fragment key={page}>
                                  {showEllipsis && (
                                    <span className={`px-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>...</span>
                                  )}
                                  <button
                                    onClick={() => handlePageChange(page)}
                                    className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${currentPage === page
                                      ? isDark
                                        ? 'bg-cyan-600 border-cyan-500 text-white'
                                        : 'bg-cyan-500 border-cyan-500 text-white'
                                      : isDark
                                        ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                      }`}
                                  >
                                    {page}
                                  </button>
                                </React.Fragment>
                              );
                            })}
                        </div>

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${currentPage === totalPages
                            ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                            : isDark
                              ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Import / Export history from Firestore */}
              <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Export History
                  </h4>
                  <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    {importExportLoading ? (
                      <p className={`p-6 text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading…</p>
                    ) : exportHistory.length === 0 ? (
                      <p className={`p-6 text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No export history recorded for this user.</p>
                    ) : (
                      <table className="min-w-full text-sm">
                        <thead className={isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                          <tr>
                            <th className="px-4 py-2 text-left">Date</th>
                            <th className="px-4 py-2 text-left">Records</th>
                            <th className="px-4 py-2 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody className={isDark ? 'divide-gray-700' : 'divide-gray-200'}>
                          {exportHistory.map((row) => (
                            <tr key={String(row.id)} className={isDark ? 'divide-gray-700' : ''}>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                {formatTimestamp(row.exportedAt as Parameters<typeof formatTimestamp>[0])}
                              </td>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {typeof row.contactCount === 'number' ? row.contactCount : '—'}
                              </td>
                              <td className="px-4 py-2 capitalize">{String(row.status || '—')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Import History
                  </h4>
                  <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    {importExportLoading ? (
                      <p className={`p-6 text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading…</p>
                    ) : importHistory.length === 0 ? (
                      <p className={`p-6 text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No import history recorded for this user.</p>
                    ) : (
                      <table className="min-w-full text-sm">
                        <thead className={isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                          <tr>
                            <th className="px-4 py-2 text-left">Date</th>
                            <th className="px-4 py-2 text-left">Records</th>
                            <th className="px-4 py-2 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importHistory.map((row) => (
                            <tr key={String(row.id)}>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                {formatTimestamp(row.importedAt as Parameters<typeof formatTimestamp>[0])}
                              </td>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {typeof row.contactCount === 'number'
                                  ? row.contactCount
                                  : typeof row.importedCount === 'number'
                                    ? row.importedCount
                                    : '—'}
                              </td>
                              <td className="px-4 py-2 capitalize">{String(row.status || '—')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment & Billing History */}
              <div className="mb-6">
                <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  💳 Payment & Billing History
                </h4>
                <div className={`rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className={`p-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          All Payments
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Complete payment and invoice history for this user
                        </div>
                      </div>
                      <div className={`px-3 py-1 text-xs font-medium rounded-lg ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                        {payments.length} payment{payments.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div className={`${isDark ? 'bg-gray-900' : 'bg-white'}`}>
                    {paymentsLoading ? (
                      <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto mb-2"></div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading payment history...</p>
                      </div>
                    ) : payments.length === 0 ? (
                      <div className="p-8 text-center">
                        <svg className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No payments found</p>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>This user hasn't made any payments yet</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                            <tr>
                              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                Date
                              </th>
                              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                Payment ID
                              </th>
                              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                Amount
                              </th>
                              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                Method
                              </th>
                              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className={`${isDark ? 'bg-gray-900 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                            {payments.map((payment) => (
                              <tr key={payment.id} className={`${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'} transition-colors`}>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {payment.createdAt?.toDate ? payment.createdAt.toDate().toLocaleString() : 'N/A'}
                                </td>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                  <span className="truncate max-w-[150px] block" title={payment.paymentId}>
                                    {payment.paymentId?.substring(0, 16)}...
                                  </span>
                                </td>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                                  {payment.currency === 'INR' ? '₹' : '$'}{(payment.amount / 100).toFixed(2)}
                                </td>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm capitalize ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                  {payment.method || 'Unknown'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${payment.status === 'captured' || payment.status === 'authorized'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : payment.status === 'failed'
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                    }`}>
                                    {payment.status === 'captured' ? 'Paid' : payment.status === 'authorized' ? 'Authorized' : payment.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Email Communication Section */}
              <div className="mb-6">
                <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  📧 Email Communication
                </h4>
                <div className={`rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className={`p-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Sent Emails
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          System and admin emails sent to this user
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`px-3 py-1 text-xs font-medium rounded-lg ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                          {emails.length} email{emails.length !== 1 ? 's' : ''}
                        </div>
                        <button
                          onClick={() => setShowEmailModal(true)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${isDark
                            ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                            : 'bg-cyan-600 text-white hover:bg-cyan-700'
                            }`}
                        >
                          ✉️ Send Email
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className={`${isDark ? 'bg-gray-900' : 'bg-white'}`}>
                    {emailsLoading ? (
                      <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto mb-2"></div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading email history...</p>
                      </div>
                    ) : emails.length === 0 ? (
                      <div className="p-8 text-center">
                        <svg className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No emails sent</p>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No system or admin emails have been sent to this user</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto max-h-64">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'} sticky top-0`}>
                            <tr>
                              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                Date
                              </th>
                              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                Subject
                              </th>
                              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                Type
                              </th>
                              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className={`${isDark ? 'bg-gray-900 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                            {emails.map((email) => (
                              <tr key={email.id} className={`${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'} transition-colors`}>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {email.timestamp?.toDate ? email.timestamp.toDate().toLocaleString() : 'N/A'}
                                </td>
                                <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                  <span className="truncate max-w-[200px] block" title={email.subject}>
                                    {email.subject}
                                  </span>
                                </td>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm capitalize ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                  <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${email.type === 'admin_message'
                                    ? (isDark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700')
                                    : email.type === 'verification'
                                      ? (isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700')
                                      : (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')
                                    }`}>
                                    {email.type.replace(/_/g, ' ')}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${email.status === 'sent'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                    }`}>
                                    {email.status === 'sent' ? '✓ Sent' : '✗ Failed'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Send Email Modal */}
              {showEmailModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                    <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={() => !sendingEmail && setShowEmailModal(false)}></div>
                    <div className={`relative z-10 w-full max-w-lg p-6 mx-auto rounded-lg shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Send Email to {user?.firstName || user?.email}
                        </h3>
                        <button
                          onClick={() => !sendingEmail && setShowEmailModal(false)}
                          disabled={sendingEmail}
                          className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            To
                          </label>
                          <input
                            type="text"
                            value={user?.email || ''}
                            disabled
                            className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-500'}`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Subject
                          </label>
                          <input
                            type="text"
                            value={emailForm.subject}
                            onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                            placeholder="Enter email subject..."
                            disabled={sendingEmail}
                            className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Message
                          </label>
                          <textarea
                            value={emailForm.body}
                            onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                            placeholder="Type your message here..."
                            rows={6}
                            disabled={sendingEmail}
                            className={`w-full px-3 py-2 rounded-lg border resize-none ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                          />
                        </div>
                      </div>

                      <div className="flex justify-end space-x-3 mt-6">
                        <button
                          onClick={() => setShowEmailModal(false)}
                          disabled={sendingEmail}
                          className={`px-4 py-2 text-sm font-medium rounded-lg ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSendEmail}
                          disabled={sendingEmail || !emailForm.subject.trim() || !emailForm.body.trim()}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${sendingEmail || !emailForm.subject.trim() || !emailForm.body.trim()
                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            : 'bg-cyan-600 text-white hover:bg-cyan-700'
                            }`}
                        >
                          {sendingEmail ? (
                            <span className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Sending...
                            </span>
                          ) : (
                            '✉️ Send Email'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Account Security Controls */}
              <div className="mb-6">
                <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  🔐 Account Security
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Force Password Reset */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg ${isDark ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-600'}`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </div>
                        <div>
                          <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Force Password Reset
                          </div>
                          <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Send a password reset email to the user
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleForcePasswordReset}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${isDark
                          ? 'bg-orange-600 text-white hover:bg-orange-700'
                          : 'bg-orange-600 text-white hover:bg-orange-700'
                          }`}
                      >
                        Send Reset Email
                      </button>
                    </div>
                  </div>

                  {/* Force Verify Email */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg ${user?.emailVerified
                          ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-600')
                          : (isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-600')
                          }`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {user?.emailVerified ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            )}
                          </svg>
                        </div>
                        <div>
                          <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Email Verification
                          </div>
                          <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {user?.emailVerified ? (
                              <span className="text-green-500">✓ Email is verified</span>
                            ) : (
                              <span className="text-yellow-500">Email not verified</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {!user?.emailVerified && (
                        <button
                          onClick={handleForceVerifyEmail}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${isDark
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                        >
                          Verify Now
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Management Section */}
              <div className="mb-6">
                <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  📊 Data Management
                </h4>
                <div className={`rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  {/* Export Controls */}
                  <div className={`p-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${isDark ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </div>
                        <div>
                          <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Export User Data
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Download all user data as JSON file
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleExportData}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isDark
                          ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                          }`}
                      >
                        📥 Export Data
                      </button>
                    </div>
                  </div>

                  {/* Activity Log */}
                  <div className={`p-4 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Recent Activity
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {activities.length} activities logged
                      </div>
                    </div>

                    {activitiesLoading ? (
                      <div className="p-6 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500 mx-auto mb-2"></div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading activities...</p>
                      </div>
                    ) : activities.length === 0 ? (
                      <div className="p-6 text-center">
                        <svg className={`w-10 h-10 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No activities recorded</p>
                      </div>
                    ) : (
                      <div className="max-h-64 overflow-y-auto">
                        <div className="space-y-2">
                          {activities.slice(0, 20).map((activity) => (
                            <div
                              key={activity.id}
                              className={`p-3 rounded-lg ${isDark ? 'bg-gray-800 hover:bg-gray-750' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3">
                                  <div className={`w-2 h-2 mt-2 rounded-full ${(activity.action || activity.type) === 'login' ? 'bg-green-500' :
                                    (activity.action || activity.type) === 'logout' ? 'bg-gray-500' :
                                      (activity.action || activity.type) === 'email_sent' ? 'bg-blue-500' :
                                        (activity.action || activity.type) === 'contact_added' ? 'bg-cyan-500' :
                                          (activity.action || activity.type) === 'campaign_created' ? 'bg-purple-500' :
                                            (activity.action || activity.type) === 'subscription_updated' ? 'bg-yellow-500' :
                                              'bg-gray-400'
                                    }`}></div>
                                  <div>
                                    <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {(activity.action || activity.type)?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'Activity'}
                                    </div>
                                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {activity.details || activity.description || 'No description'}
                                    </div>
                                  </div>
                                </div>
                                <div className={`text-xs whitespace-nowrap ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {activity.timestamp?.toDate ? activity.timestamp.toDate().toLocaleDateString() : 'N/A'}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                  }`}>
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-600'
                      }`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Add Contact
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Create new contact
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                  }`}>
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
                      }`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Bulk Actions
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Manage multiple contacts
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                  }`}>
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                      }`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Analytics
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Contact insights
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                  }`}>
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-100 text-orange-600'
                      }`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Settings
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Contact preferences
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile & Account Settings Section */}
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Profile & Account Settings
                </h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Profile Information */}
                <div className="space-y-6">
                  <h4 className={`text-md font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Profile Information
                  </h4>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                          Display Name
                        </label>
                        <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {user?.displayName || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                          Company Name
                        </label>
                        <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {user?.companyName || 'Not set'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                          Industry
                        </label>
                        <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {user?.industry || 'Not specified'}
                        </p>
                      </div>
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                          Onboarding Status
                        </label>
                        <div className="mt-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user?.onboardingCompleted
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {user?.onboardingCompleted ? 'Completed' : 'Pending'}
                          </span>
                        </div>
                      </div>
                      {(user as any)?.signupSource && (
                        <div>
                          <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                            Signup Source
                          </label>
                          <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {(user as any).signupSourceLabel || (user as any).signupSource}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Account Settings */}
                <div className="space-y-6">
                  <h4 className={`text-md font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Account Settings
                  </h4>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                          Two-Factor Auth
                        </label>
                        <div className="mt-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.twoFactorEnabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                            }`}>
                            {user.twoFactorEnabled ? 'Enabled' : 'Not enabled'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                          Timezone
                        </label>
                        <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {profileSettings.timezone}
                        </p>
                      </div>
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                          Language
                        </label>
                        <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {profileSettings.language}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notification Preferences */}
              <div className="mt-8">
                <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Notification Preferences
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Email Notifications
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Receive email updates
                        </div>
                      </div>
                      <div className={`w-12 h-6 rounded-full flex items-center transition-colors ${profileSettings.emailNotifications
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                        }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${profileSettings.emailNotifications ? 'translate-x-6' : 'translate-x-0.5'
                          }`}></div>
                      </div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          SMS Notifications
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Receive SMS alerts
                        </div>
                      </div>
                      <div className={`w-12 h-6 rounded-full flex items-center transition-colors ${profileSettings.smsNotifications
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                        }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${profileSettings.smsNotifications ? 'translate-x-6' : 'translate-x-0.5'
                          }`}></div>
                      </div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Marketing Emails
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Receive promotional content
                        </div>
                      </div>
                      <div className={`w-12 h-6 rounded-full flex items-center transition-colors ${profileSettings.marketingEmails
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                        }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${profileSettings.marketingEmails ? 'translate-x-6' : 'translate-x-0.5'
                          }`}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* API keys & Billing */}
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    API & Webhooks
                  </h4>
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>API keys</span>
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {profileSettingsLoaded ? profileSettings.apiKeys : '—'}
                        </span>
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Zapier webhooks</span>
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {profileSettingsLoaded ? profileSettings.webhooks : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Billing Information */}
                <div>
                  <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Billing Information
                  </h4>
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Current Plan
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {profileSettings.planType} - {profileSettings.billingCycle}
                          </div>
                        </div>
                        <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {profileSettings.planType}
                        </div>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Storage Usage
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {profileSettings.storageUsed} of {profileSettings.storageLimit}
                          </div>
                        </div>
                        {profileSettings.storageLimit !== '—' && (
                          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            {(() => {
                              const used = storageUsagePercent(profileSettings.storageUsed, profileSettings.storageLimit);
                              return used !== null ? `${used}%` : '—';
                            })()}
                          </div>
                        )}
                      </div>
                      {profileSettings.storageLimit !== '—' && (
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${storageUsagePercent(profileSettings.storageUsed, profileSettings.storageLimit) || 0}%`,
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Next Billing Date
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {profileSettings.paymentMethod}
                          </div>
                        </div>
                        <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {profileSettings.nextBillingDate ? profileSettings.nextBillingDate.toLocaleDateString() : 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Integrations & Webhooks */}
              <div className="mt-8">
                <h4 className={`text-md font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Integrations & Webhooks
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Connected Integrations
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {profileSettings.integrations.length > 0
                            ? profileSettings.integrations.join(', ')
                            : 'None connected'}
                        </div>
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {profileSettings.integrations.length} active
                      </div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Active Webhooks
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Configured webhook endpoints
                        </div>
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {profileSettings.webhooks} configured
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Automations */}
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Automations ({automations.length})
              </h3>
              {automationsLoading ? (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</p>
              ) : automations.length === 0 ? (
                <p className={`text-sm italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No automations found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className={`min-w-full text-sm divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Trigger</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Created</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {automations.map((a: any) => (
                        <tr key={a.id} className={isDark ? 'bg-gray-900' : 'bg-white'}>
                          <td className="px-4 py-2 font-medium">{a.name || '\u2014'}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${a.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                              {a.status || '\u2014'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs">{a.trigger?.type || a.triggerType || '\u2014'}</td>
                          <td className="px-4 py-2 text-xs text-gray-400">{a.createdAt?.toDate?.()?.toLocaleDateString() || '\u2014'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Sender Identities */}
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Sender Identities
              </h3>
              {sendersLoading ? (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Verified Emails ({senderEmails.length})
                    </h4>
                    {senderEmails.length === 0 ? (
                      <p className={`text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>None</p>
                    ) : (
                      <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        {senderEmails.map((e: any) => (
                          <div key={e.id} className={`flex items-center justify-between px-4 py-2.5 text-sm border-b last:border-0 ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-100 bg-white'}`}>
                            <span>{e.email}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${e.status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {e.status || 'pending'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Verified Domains ({senderDomains.length})
                    </h4>
                    {senderDomains.length === 0 ? (
                      <p className={`text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>None</p>
                    ) : (
                      <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        {senderDomains.map((d: any) => (
                          <div key={d.id} className={`flex items-center justify-between px-4 py-2.5 text-sm border-b last:border-0 ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-100 bg-white'}`}>
                            <span className="font-mono">{d.domain}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${d.status === 'verified' ? 'bg-green-100 text-green-700' : d.status === 'warming' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                              {d.status || 'pending'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* API Keys */}
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                API Keys ({apiKeysList.filter((k: any) => k.isActive).length} active / {apiKeysList.length} total)
              </h3>
              {apiKeysListLoading ? (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</p>
              ) : apiKeysList.length === 0 ? (
                <p className={`text-sm italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No API keys found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className={`min-w-full text-sm divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Preview</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Last Used</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Created</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {apiKeysList.map((k: any) => (
                        <tr key={k.id} className={`${isDark ? 'bg-gray-900' : 'bg-white'} ${!k.isActive ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-2 font-medium">{k.name || '\u2014'}</td>
                          <td className="px-4 py-2 font-mono text-xs">{k.keyPreview || '\u2014'}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${k.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {k.isActive ? 'Active' : 'Revoked'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-400">{k.lastUsedAt ? k.lastUsedAt.toDate?.()?.toLocaleString() : 'Never'}</td>
                          <td className="px-4 py-2 text-xs text-gray-400">{k.createdAt?.toDate?.()?.toLocaleDateString() || '\u2014'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Abuse Flags */}
          {abuseFlags.length > 0 && (
            <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-red-950/20 border-red-900/40' : 'bg-red-50 border-red-200'}`}>
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4 text-red-600 dark:text-red-400">
                  Abuse Flags ({abuseFlags.filter((f: any) => !f.resolved).length} unresolved)
                </h3>
                <div className="space-y-2">
                  {abuseFlags.map((f: any) => (
                    <div key={f.id} className={`flex items-start justify-between p-3 rounded-lg ${f.resolved ? (isDark ? 'bg-gray-800' : 'bg-gray-100') : (isDark ? 'bg-red-900/20' : 'bg-red-100')}`}>
                      <div>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${f.resolved ? 'bg-gray-200 text-gray-600' : 'bg-red-200 text-red-700'}`}>
                          {f.type || 'unknown'}
                        </span>
                        {f.resolved && <span className="ml-2 text-xs text-gray-400">Resolved</span>}
                        <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{f.detail}</p>
                      </div>
                      <span className={`text-xs whitespace-nowrap ml-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {f.flaggedAt?.toDate?.()?.toLocaleString() || '\u2014'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Signup Attribution */}
          <SignupAttributionCard user={user as any} isDark={isDark} />

          {/* Admin Action Audit Trail */}
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="p-6">
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Admin Action History ({adminActions.length})
              </h3>
              {adminActionsLoading ? (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</p>
              ) : adminActions.length === 0 ? (
                <p className={`text-sm italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No admin actions recorded.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className={`min-w-full text-sm divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Timestamp</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Action</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Admin</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase">Note</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {adminActions.map((a: any) => (
                        <tr key={a.id} className={isDark ? 'bg-gray-900' : 'bg-white'}>
                          <td className="px-4 py-2 text-xs text-gray-400 whitespace-nowrap">{a.timestamp?.toDate?.()?.toLocaleString() || '\u2014'}</td>
                          <td className="px-4 py-2 font-medium">{String(a.action || '').replace(/_/g, ' ')}</td>
                          <td className="px-4 py-2 text-xs">{a.adminEmail || a.adminId || '\u2014'}</td>
                          <td className={`px-4 py-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{a.adminNote || a.reason || '\u2014'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Activity Log Section */}
          <div className={`mt-8 rounded-lg shadow-sm border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Activity Log
                  </h3>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Complete history of user activities and actions
                  </p>
                </div>
                {activities.length > 0 && (
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {activities.length} {activities.length === 1 ? 'activity' : 'activities'}
                  </div>
                )}
              </div>

              {activitiesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className={`ml-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading activities...</span>
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-12">
                  <svg className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    No activities recorded yet
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                          Time
                        </th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                          Action
                        </th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'
                      }`}>
                      {currentActivities.map((activity) => {
                        const timestamp = activity.timestamp?.toDate();
                        const formatTime = (date: Date) => {
                          const now = new Date();
                          const diffMs = now.getTime() - date.getTime();
                          const diffMins = Math.floor(diffMs / 60000);
                          const diffHours = Math.floor(diffMs / 3600000);
                          const diffDays = Math.floor(diffMs / 86400000);

                          if (diffMins < 1) return 'Just now';
                          if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
                          if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
                          if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
                          return date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                        };

                        const getActionIcon = (action: string) => {
                          const actionLower = action.toLowerCase();
                          if (actionLower.includes('login') || actionLower.includes('sign in')) {
                            return (
                              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                              </svg>
                            );
                          } else if (actionLower.includes('campaign') || actionLower.includes('email')) {
                            return (
                              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            );
                          } else if (actionLower.includes('contact') || actionLower.includes('subscriber')) {
                            return (
                              <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            );
                          } else if (actionLower.includes('settings') || actionLower.includes('update')) {
                            return (
                              <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            );
                          } else {
                            return (
                              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            );
                          }
                        };

                        return (
                          <tr key={activity.id} className={`hover:${isDark ? 'bg-gray-800' : 'bg-gray-50'} transition-colors`}>
                            <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'
                              }`}>
                              <div className="flex items-center">
                                {timestamp && (
                                  <>
                                    <span className="font-medium">{formatTime(timestamp)}</span>
                                    <span className={`ml-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                      {timestamp.toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className={`px-4 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                              <div className="flex items-center space-x-2">
                                {getActionIcon(activity.action)}
                                <span className="font-medium">{activity.action}</span>
                              </div>
                            </td>
                            <td className={`px-4 py-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                              {activity.details}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Activities Pagination Controls */}
              {activities.length > activitiesPerPage && (
                <div className={`mt-4 px-4 py-4 border-t ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                  }`}>
                  <div className="flex items-center justify-between">
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Showing {indexOfFirstActivity + 1} to {Math.min(indexOfLastActivity, activities.length)} of {activities.length} activities
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleActivitiesPageChange(activitiesPage - 1)}
                        disabled={activitiesPage === 1}
                        className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${activitiesPage === 1
                          ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                          : isDark
                            ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        Previous
                      </button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.ceil(activities.length / activitiesPerPage) }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first page, last page, current page, and pages around current
                            const totalPages = Math.ceil(activities.length / activitiesPerPage);
                            return page === 1 ||
                              page === totalPages ||
                              (page >= activitiesPage - 1 && page <= activitiesPage + 1);
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const prevPage = array[index - 1];
                            const showEllipsis = prevPage && page - prevPage > 1;
                            return (
                              <React.Fragment key={page}>
                                {showEllipsis && (
                                  <span className={`px-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>...</span>
                                )}
                                <button
                                  onClick={() => handleActivitiesPageChange(page)}
                                  className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${activitiesPage === page
                                    ? isDark
                                      ? 'bg-cyan-600 border-cyan-500 text-white'
                                      : 'bg-cyan-500 border-cyan-500 text-white'
                                    : isDark
                                      ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          })}
                      </div>
                      <button
                        onClick={() => handleActivitiesPageChange(activitiesPage + 1)}
                        disabled={activitiesPage >= Math.ceil(activities.length / activitiesPerPage)}
                        className={`px-3 py-1 text-sm font-medium rounded-lg border transition-colors ${activitiesPage >= Math.ceil(activities.length / activitiesPerPage)
                          ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                          : isDark
                            ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsPage;